// src/utils/sightings/corrections.js
import { doc, serverTimestamp, writeBatch, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const PER_SPECIES_COLLECTION = 'perSpecies';

const RELOCATE_MEDIA_ENDPOINT =
  process.env.REACT_APP_RELOCATE_MEDIA_ENDPOINT || '';

const sanitizePathSegments = (path) =>
  typeof path === 'string'
    ? path
        .split('/')
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0)
    : [];

const toDocRef = (path) => {
  const segments = sanitizePathSegments(path);
  if (segments.length < 2 || segments.length % 2 !== 0) {
    throw new Error('Invalid document path provided for sighting correction.');
  }
  return doc(db, ...segments);
};

const slugify = (value) => {
  if (typeof value !== 'string') return '';
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const toTitleCase = (value) => {
  if (typeof value !== 'string' || value.trim().length === 0) return 'Unknown';
  return value
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const normalizeCustomSpeciesLabel = (value) => (
  typeof value === 'string'
    ? value.trim().replace(/\s+/g, ' ')
    : ''
);

const toSpeciesToken = (value) => normalizeCustomSpeciesLabel(value)
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const CANONICAL_SPECIES_NAMES = [
  'leopard',
  'lion',
  'cheetah',
  'wild dog',
  'spotted hyena',
  'elephant',
  'buffalo',
  'african civet',
  'serval',
  'verreaux eagle owl',
  'martial eagle',
  'burchells zebra',
  'yellow billed oxpecker',
  'southern ground hornbill',
  'giraffe',
  'impala',
  'waterbuck',
  'goliath heron',
  'white backed vulture',
  'brown hyena',
  'warthog',
  'egyptian goose',
  'hippopotamus',
  'sable antelope',
  'honey badger',
  'bird',
  'nile crocodile',
  'ostrich',
  'bateleur',
  'pearl spotted owl',
  'caracal',
  'wildebeest',
  'black backed jackal',
  'chacma baboon',
  'leopard tortoise',
  'kudu',
  'pangolin',
  'genet',
  'raptor',
  'porcupine',
  'crowned lapwing',
  'tsessebe',
  'cattle egret',
  'guineafowl',
  'tawny eagle',
  'steenbok',
  'white stork',
  'kori bustard',
  'klipspringer',
  'red-billed buffalo weaver',
  'grey heron',
  'fish',
  'african fish eagle',
  'eland',
  'python',
  'puff adder',
  'brown snake eagle',
  'blacksmith lapwing',
  'serrated terrapin',
  'house sparrow',
  'vervet monkey',
  'bushbuck',
  'duiker',
];

const SPECIES_ALIASES = {
  hippo: 'hippopotamus',
  hippos: 'hippopotamus',
  hippopotami: 'hippopotamus',
  capybara: 'hippopotamus',
  hyena: 'spotted hyena',
  hyenas: 'spotted hyena',
  'wild dogs': 'wild dog',
  'african wild dog': 'wild dog',
  'african wild dogs': 'wild dog',
  'african buffalo': 'buffalo',
  'cape buffalo': 'buffalo',
};

const pluralLastWordTokens = (token) => {
  const parts = token.split(' ').filter(Boolean);
  if (parts.length === 0) return [];

  const last = parts[parts.length - 1];
  const prefix = parts.slice(0, -1);
  const variants = new Set([`${last}s`, `${last}es`]);
  if (last.endsWith('y') && !/[aeiou]y$/.test(last)) {
    variants.add(`${last.slice(0, -1)}ies`);
  }

  return Array.from(variants).map((variant) => [...prefix, variant].join(' '));
};

const KNOWN_SPECIES_LOOKUP = (() => {
  const lookup = {};

  CANONICAL_SPECIES_NAMES.forEach((name) => {
    const canonical = normalizeCustomSpeciesLabel(name).toLowerCase().replace(/[_]+/g, ' ');
    const token = toSpeciesToken(canonical);
    if (!token) return;

    lookup[token] = canonical;
    pluralLastWordTokens(token).forEach((pluralToken) => {
      lookup[pluralToken] = canonical;
    });
  });

  Object.entries(SPECIES_ALIASES).forEach(([alias, canonicalName]) => {
    const aliasToken = toSpeciesToken(alias);
    const canonicalToken = toSpeciesToken(canonicalName);
    const canonical = lookup[canonicalToken] || normalizeCustomSpeciesLabel(canonicalName).toLowerCase();
    if (aliasToken && canonical) {
      lookup[aliasToken] = canonical;
    }
  });

  return lookup;
})();

const getDamerauLevenshteinDistance = (firstValue, secondValue) => {
  const first = String(firstValue || '');
  const second = String(secondValue || '');
  const rows = first.length + 1;
  const columns = second.length + 1;
  const distances = Array.from(
    { length: rows },
    () => Array.from({ length: columns }, () => 0),
  );

  for (let row = 0; row < rows; row += 1) {
    distances[row][0] = row;
  }
  for (let column = 0; column < columns; column += 1) {
    distances[0][column] = column;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let column = 1; column < columns; column += 1) {
      const substitutionCost = first[row - 1] === second[column - 1] ? 0 : 1;
      distances[row][column] = Math.min(
        distances[row - 1][column] + 1,
        distances[row][column - 1] + 1,
        distances[row - 1][column - 1] + substitutionCost,
      );

      if (
        row > 1
        && column > 1
        && first[row - 1] === second[column - 2]
        && first[row - 2] === second[column - 1]
      ) {
        distances[row][column] = Math.min(
          distances[row][column],
          distances[row - 2][column - 2] + 1,
        );
      }
    }
  }

  return distances[first.length][second.length];
};

const getSpellingDistanceLimit = (length) => {
  if (length <= 4) return 1;
  if (length <= 8) return 2;
  return Math.min(3, Math.floor(length * 0.25));
};

export const getSpeciesSpellingSuggestion = (species) => {
  const input = normalizeCustomSpeciesLabel(species);
  const inputToken = toSpeciesToken(input);
  if (!inputToken || KNOWN_SPECIES_LOOKUP[inputToken]) {
    return null;
  }

  const distanceLimit = getSpellingDistanceLimit(inputToken.length);
  let closestMatch = null;

  Object.entries(KNOWN_SPECIES_LOOKUP).forEach(([knownToken, canonicalName]) => {
    if (Math.abs(inputToken.length - knownToken.length) > distanceLimit) {
      return;
    }

    const distance = getDamerauLevenshteinDistance(inputToken, knownToken);
    if (
      distance <= distanceLimit
      && (!closestMatch || distance < closestMatch.distance)
    ) {
      closestMatch = { canonicalName, distance };
    }
  });

  if (!closestMatch) {
    return null;
  }

  return {
    input,
    suggestion: toTitleCase(closestMatch.canonicalName),
    distance: closestMatch.distance,
  };
};

export const normalizeSightingSpeciesInput = (species) => {
  const cleaned = normalizeCustomSpeciesLabel(species) || 'unknown';
  const token = toSpeciesToken(cleaned);
  const canonical = token ? KNOWN_SPECIES_LOOKUP[token] : '';
  const resolved = canonical || cleaned;
  const slug = slugify(resolved) || 'unknown';

  return {
    normalizedName: resolved,
    slug,
    label: canonical ? toTitleCase(canonical) : resolved,
    folderLabel: slug,
    isKnownSpecies: Boolean(canonical),
    cooldownSpecies: canonical || null,
  };
};

const comparableSpeciesKey = (value) => normalizeSightingSpeciesInput(value).normalizedName.toLowerCase();

const mergeNotes = (existing, nextNote) => {
  const trimmedExisting = typeof existing === 'string' ? existing.trim() : '';
  if (!trimmedExisting) return nextNote || '';
  if (!nextNote) return trimmedExisting;
  return `${trimmedExisting}\n${nextNote}`;
};

const stripClientFields = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  const clone = { ...data };
  delete clone.id;
  delete clone.deletedAt;
  delete clone.deletedBy;
  return clone;
};

const buildRenamedEntityTally = ({ entityTally, fromKey, toKey }) => {
  if (!entityTally || typeof entityTally !== 'object' || Array.isArray(entityTally)) {
    return null;
  }

  const keys = Object.keys(entityTally);
  if (keys.length === 0) return null;

  const normalizedFromKey = fromKey ? comparableSpeciesKey(fromKey) : '';
  const resolvedFromKey =
    (fromKey && Object.prototype.hasOwnProperty.call(entityTally, fromKey) && fromKey) ||
    (normalizedFromKey
      ? keys.find((key) => comparableSpeciesKey(key) === normalizedFromKey)
      : null) ||
    (keys.length === 1 ? keys[0] : null);

  if (!resolvedFromKey) return null;
  if (resolvedFromKey === toKey) return entityTally;

  const next = { ...entityTally };
  const valueToMove = next[resolvedFromKey];
  delete next[resolvedFromKey];

  if (Object.prototype.hasOwnProperty.call(next, toKey)) {
    const existingValue = next[toKey];
    if (typeof existingValue === 'number' && typeof valueToMove === 'number') {
      next[toKey] = existingValue + valueToMove;
    } else {
      next[toKey] = valueToMove;
    }
  } else {
    next[toKey] = valueToMove;
  }

  return next;
};

export const describeSpeciesChange = ({ mode, species }) => {
  if (mode === 'background') {
    return {
      normalizedName: 'background',
      slug: 'background',
      label: 'Background',
      folderLabel: 'background',
    };
  }

  return normalizeSightingSpeciesInput(species);
};

export const buildCorrectionNote = ({
  actor,
  previousSpecies,
  nextLabel,
  locationId,
  includeTimestamp = true,
  timestamp = new Date(),
}) => {
  const actorName = typeof actor === 'string' && actor.trim().length > 0 ? actor.trim() : 'Admin';
  const prev = typeof previousSpecies === 'string' && previousSpecies.trim().length > 0
    ? previousSpecies.trim()
    : 'Unknown';
  const next = typeof nextLabel === 'string' && nextLabel.trim().length > 0
    ? nextLabel.trim()
    : 'Unknown';

  const locationFragment = typeof locationId === 'string' && locationId.trim().length > 0
    ? ` at ${locationId.trim()}`
    : '';

  const normalizedTimestamp = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const timestampPrefix = includeTimestamp ? `${normalizedTimestamp.toISOString()} – ` : '';

  if (prev.toLowerCase() === next.toLowerCase()) {
    return `${timestampPrefix}${actorName} reaffirmed ${next}${locationFragment}.`.trim();
  }

  return `${timestampPrefix}${actorName} corrected ${prev} to ${next}${locationFragment}.`.trim();
};

const relocateSightingMedia = async ({ parentPath, targetSpecies, authToken }) => {
  if (!RELOCATE_MEDIA_ENDPOINT) {
    return null;
  }
  if (!authToken) {
    throw new Error('Missing auth token for media relocation.');
  }

  const response = await fetch(RELOCATE_MEDIA_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      parentPath,
      targetSpecies,
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json')
    ? await response.json().catch(() => ({}))
    : await response.text();

  if (!response.ok) {
    const message = typeof body === 'string' ? body : body?.error;
    throw new Error(message || 'Failed to relocate sighting media.');
  }

  return body; // { ok: true, updates: {...} }
};

export const applySightingCorrection = async ({
  entry,
  mode,
  nextSpeciesName,
  actor,
  note,
  change: providedChange,

  // NEW:
  relocateMedia = false,
  authToken = null,
}) => {
  if (!entry?.meta?.parentPath) {
    throw new Error('Sighting metadata is missing required references.');
  }
  if (!entry?.meta?.speciesDocPath) {
    throw new Error('Sighting metadata is missing the perSpecies document reference.');
  }

  const change = providedChange || describeSpeciesChange({ mode, species: nextSpeciesName });

  const parentDocData = entry.meta.parentDoc || {};
  const speciesDocData = entry.meta.speciesDoc || {};

  const oldSpeciesKey =
    (typeof speciesDocData.species === 'string' && speciesDocData.species.trim()) ||
    (typeof entry.species === 'string' && entry.species.trim()) ||
    '';

  const nextSpeciesKey = change.normalizedName;

  const finalNote =
    note ||
    buildCorrectionNote({
      actor,
      previousSpecies: oldSpeciesKey || entry.species,
      nextLabel: change.label,
      locationId: entry.locationId || parentDocData.locationId,
    });

  const parentRef = toDocRef(entry.meta.parentPath);
  const oldSpeciesRef = toDocRef(entry.meta.speciesDocPath);

  const oldSpeciesDocId =
    (typeof speciesDocData.id === 'string' && speciesDocData.id) ||
    sanitizePathSegments(entry.meta.speciesDocPath).slice(-1)[0];

  const nextSpeciesDocId = change.slug || oldSpeciesDocId;
  const shouldRetargetHighlightSource =
    typeof parentDocData.highlightSourceSpeciesDocId === 'string'
    && parentDocData.highlightSourceSpeciesDocId === oldSpeciesDocId
    && nextSpeciesDocId !== oldSpeciesDocId;

  const batch = writeBatch(db);

  // ----- Parent doc updates -----
  const parentUpdatesFirestore = {
    corrected: true,
    updatedAt: serverTimestamp(),
  };
  const parentUpdatesState = {
    corrected: true,
  };

  const noteField = Object.prototype.hasOwnProperty.call(parentDocData, 'reviewNotes')
    ? 'reviewNotes'
    : (Object.prototype.hasOwnProperty.call(parentDocData, 'notes') ? 'notes' : 'reviewNotes');

  const mergedNote = mergeNotes(parentDocData?.[noteField], finalNote);
  parentUpdatesFirestore[noteField] = mergedNote;
  parentUpdatesState[noteField] = mergedNote;

  const nextTally = buildRenamedEntityTally({
    entityTally: parentDocData.entityTally,
    fromKey: oldSpeciesKey,
    toKey: nextSpeciesKey,
  });

  if (nextTally) {
    parentUpdatesFirestore.entityTally = nextTally;
    parentUpdatesState.entityTally = nextTally;
  }

  if (typeof parentDocData.primarySpecies === 'string' && parentDocData.primarySpecies.trim()) {
    const primary = parentDocData.primarySpecies.trim();
    if (
      oldSpeciesKey
      && comparableSpeciesKey(primary) === comparableSpeciesKey(oldSpeciesKey)
      && comparableSpeciesKey(primary) !== comparableSpeciesKey(nextSpeciesKey)
    ) {
      parentUpdatesFirestore.primarySpecies = nextSpeciesKey;
      parentUpdatesState.primarySpecies = nextSpeciesKey;
    }
  } else if (Object.prototype.hasOwnProperty.call(parentDocData, 'primarySpecies')) {
    parentUpdatesFirestore.primarySpecies = nextSpeciesKey;
    parentUpdatesState.primarySpecies = nextSpeciesKey;
  }

  if (shouldRetargetHighlightSource) {
    parentUpdatesFirestore.highlightSourceSpeciesDocId = nextSpeciesDocId;
    parentUpdatesState.highlightSourceSpeciesDocId = nextSpeciesDocId;
  }

  batch.update(parentRef, parentUpdatesFirestore);

  // ----- perSpecies doc update / recreation -----
  let nextSpeciesDocPath = entry.meta.speciesDocPath;

  if (nextSpeciesDocId === oldSpeciesDocId) {
    // Same doc id -> update in place
    batch.update(oldSpeciesRef, {
      species: nextSpeciesKey,
      corrected: true,
      correctedBy: actor || null,
      updatedAt: serverTimestamp(),
    });
  } else {
    const parentSegments = sanitizePathSegments(entry.meta.parentPath);
    const newSpeciesRef = doc(db, ...parentSegments, PER_SPECIES_COLLECTION, nextSpeciesDocId);
    nextSpeciesDocPath = newSpeciesRef.path;

    const clonedSpeciesData = stripClientFields(speciesDocData);
    delete clonedSpeciesData.locationId;

    const clientId =
      (typeof clonedSpeciesData.clientId === 'string' && clonedSpeciesData.clientId.trim()) ||
      (typeof entry.locationId === 'string' && entry.locationId.trim()) ||
      (typeof parentDocData.clientId === 'string' && parentDocData.clientId.trim()) ||
      (typeof parentDocData.locationId === 'string' && parentDocData.locationId.trim()) ||
      null;
    const cameraId =
      (typeof clonedSpeciesData.cameraId === 'string' && clonedSpeciesData.cameraId.trim()) ||
      (typeof parentDocData.cameraId === 'string' && parentDocData.cameraId.trim()) ||
      null;

    const newSpeciesDocData = {
      ...clonedSpeciesData,
      species: nextSpeciesKey,
      corrected: true,
      correctedBy: actor || null,
      updatedAt: serverTimestamp(),
      ...(shouldRetargetHighlightSource ? { highlightSourceSpeciesDocId: nextSpeciesDocId } : {}),
      ...(clientId ? { clientId } : {}),
      ...(cameraId ? { cameraId } : {}),
      ...(!clonedSpeciesData.createdAt && parentDocData.createdAt ? { createdAt: parentDocData.createdAt } : {}),
    };

    // Create/overwrite the new doc
    batch.set(newSpeciesRef, newSpeciesDocData);
  }

  await batch.commit();

  // Hard delete old doc if we renamed (doc id changed)
  if (nextSpeciesDocId !== oldSpeciesDocId) {
    await deleteDoc(oldSpeciesRef);
  }

  // ----- NEW: relocate media in GCS + update Firestore urls/paths -----
  // Only relocate if the parent doc is now primarily classified as nextSpeciesKey.
  // This prevents moving storage when editing a non-primary perSpecies on multi-species sightings.
  const parentPrimaryAfter =
    typeof parentUpdatesState.primarySpecies === 'string' && parentUpdatesState.primarySpecies.trim()
      ? parentUpdatesState.primarySpecies.trim().toLowerCase()
      : (typeof parentDocData.primarySpecies === 'string' && parentDocData.primarySpecies.trim()
          ? parentDocData.primarySpecies.trim().toLowerCase()
          : null);

  const shouldRelocate =
    Boolean(relocateMedia)
    && Boolean(authToken)
    && Boolean(RELOCATE_MEDIA_ENDPOINT)
    && Boolean(entry?.meta?.parentPath)
    && parentPrimaryAfter === String(nextSpeciesKey).toLowerCase();

  let relocationError = null;

  if (shouldRelocate) {
    try {
      const relocation = await relocateSightingMedia({
        parentPath: entry.meta.parentPath,
        targetSpecies: nextSpeciesKey,
        authToken,
      });

      const updates = relocation?.updates || null;
      if (updates && typeof updates === 'object') {
        // Merge so UI can immediately use updated mediaUrl/previewUrl/debugUrl
        Object.assign(parentUpdatesState, updates);
      }
    } catch (err) {
      relocationError = err?.message || 'Media relocation failed.';
      // Do not throw: the Firestore correction already succeeded.
      // UI can surface this warning and you can retry.
      // eslint-disable-next-line no-console
      console.warn('Media relocation failed', err);
    }
  }

  return {
    change,
    note: finalNote,
    parentDocUpdates: parentUpdatesState,
    speciesDocUpdates: {
      species: nextSpeciesKey,
      corrected: true,
      correctedBy: actor || null,
    },
    speciesDocId: nextSpeciesDocId,
    speciesDocPath: nextSpeciesDocPath,
    mediaRelocationError: relocationError,
  };
};

export default applySightingCorrection;
